/**
 * MATCON category definitions for MOC Daily Call reporting.
 * MC  = Mission Capable (UP, XC, FCF, DET)
 * FMC = Fully Mission Capable (UP)
 */

export const MC_STATUSES = ['UP', 'XC', 'FCF', 'DET'];

export const MATCON_CATEGORIES = [
    { key: 'FMC', label: 'FMC', statuses: ['UP'], color: '#28a745' },
    { key: 'NMCM-S', label: 'NMCM-S', statuses: ['PM'], color: '#fd7e14' },
    { key: 'NMCM-U', label: 'NMCM-U', statuses: ['DN'], color: '#cd853f' },
    { key: 'NMCS', label: 'NMCS', statuses: ['AWP'], color: '#e83e8c' },
    { key: 'OOR', label: 'OOR', statuses: ['OTH', 'REI', 'ACI', 'AIMD'], color: '#17a2b8' },
];

/** OOR detail rows for site breakdown (AWP is under NMCS, not listed here). */
export const OOR_SUB_STATUSES = ['OTH', 'REI', 'ACI', 'AIMD'];

/** Display name for a site code (uses the catalog name as-is). */
export const SITE_DISPLAY_NAMES = {};

export const SITE_KEYS = [];

function uniqueSites(records, field = 'site') {
    const names = records
        .map((record) => record && record[field])
        .filter(Boolean)
        .map((name) => String(name));
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
}

export function displaySiteName(site) {
    return SITE_DISPLAY_NAMES[site] || site || '';
}

const categoryForStatus = (status) =>
    MATCON_CATEGORIES.find((cat) => cat.statuses.includes(status));

export function normalizeStatusRecord(record) {
    const status = record.status || '';
    const site = record.site || record.aircraft?.site_name || 'Unknown';
    const tms = record.tms || record.aircraft?.tms_name || record.aircraft?.tms || 'Unknown';
    return { ...record, status, site, tms };
}

export function filterStatusRecords(records, { tms = 'All' } = {}) {
    let filtered = records.map(normalizeStatusRecord);

    if (tms && tms !== 'All') {
        filtered = filtered.filter((r) => r.tms === tms);
    }
    return filtered;
}

export function countByMatconCategory(records) {
    const counts = Object.fromEntries(MATCON_CATEGORIES.map((c) => [c.key, 0]));
    let mc = 0;
    let unmapped = 0;

    records.forEach((record) => {
        const status = record.status || '';
        if (MC_STATUSES.includes(status)) mc += 1;
        const cat = categoryForStatus(status);
        if (cat) counts[cat.key] += 1;
        else if (status && !MC_STATUSES.includes(status)) unmapped += 1;
    });

    const total = records.length;
    const matconTotal = MATCON_CATEGORIES.reduce((sum, c) => sum + counts[c.key], 0);
    const reportingTotal = total - counts.OOR;

    return {
        ...counts,
        MC: mc,
        FMC: counts.FMC,
        total,
        matconTotal,
        reportingTotal,
        unmapped,
    };
}

export function countBySiteAndMatcon(records) {
    const sites = uniqueSites(records);
    const bySite = {};
    sites.forEach((site) => {
        bySite[site] = Object.fromEntries(MATCON_CATEGORIES.map((c) => [c.key, 0]));
        bySite[site].total = 0;
    });

    records.forEach((record) => {
        const site = record.site;
        if (!bySite[site]) return;
        bySite[site].total += 1;
        const cat = categoryForStatus(record.status);
        if (cat) bySite[site][cat.key] += 1;
    });

    const totals = Object.fromEntries(MATCON_CATEGORIES.map((c) => [c.key, 0]));
    totals.total = 0;
    sites.forEach((site) => {
        MATCON_CATEGORIES.forEach((c) => {
            totals[c.key] += bySite[site][c.key];
        });
        totals.total += bySite[site].total;
    });

    return { bySite, totals };
}

export function countOorBreakdownBySite(records) {
    const emptyStatus = () => Object.fromEntries(OOR_SUB_STATUSES.map((s) => [s, 0]));
    const sites = uniqueSites(records);
    const bySite = {};
    sites.forEach((site) => {
        bySite[site] = emptyStatus();
    });
    const totals = emptyStatus();

    records.forEach((record) => {
        const site = record.site;
        const status = record.status || '';
        if (!bySite[site] || !OOR_SUB_STATUSES.includes(status)) return;
        bySite[site][status] += 1;
        totals[status] += 1;
    });

    return { bySite, totals };
}

export function computeMcTarget(reportingTotal, mcTargetOverride) {
    if (mcTargetOverride != null && !Number.isNaN(mcTargetOverride)) {
        return mcTargetOverride;
    }
    return Math.round(reportingTotal * 0.618);
}

export function computeAwaitingPartsMetrics(nmcsRecords) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let noEdd = 0;
    let over300Edd = 0;

    nmcsRecords.forEach((row) => {
        if (!row.est_delv_dt) {
            noEdd += 1;
            return;
        }
        const edd = new Date(row.est_delv_dt);
        edd.setHours(0, 0, 0, 0);
        const daysOut = Math.floor((edd - today) / (1000 * 60 * 60 * 24));
        if (daysOut > 300) over300Edd += 1;
    });

    return {
        awaitingPartsTotal: nmcsRecords.length,
        awaitingPartsNoEdd: noEdd,
        awaitingPartsOver300Edd: over300Edd,
    };
}

const formatSnapshotDate = (dateStr) => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return dateStr;
};

/** Build chart series from GET /matcon-snapshots/ rows. */
export function buildTrendFromSnapshots(snapshots) {
    if (!snapshots || snapshots.length < 2) return null;
    const sorted = [...snapshots].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
    return {
        labels: sorted.map((row) => formatSnapshotDate(row.snapshot_date)),
        datasets: {
            MC: sorted.map((row) => row.mc),
            NMCS: sorted.map((row) => row.nmcs),
            'NMCM-S': sorted.map((row) => row.nmcm_s),
            'NMCM-U': sorted.map((row) => row.nmcm_u),
        },
    };
}

/** MC change vs previous stored snapshot day. */
export function computeMcChangeFromSnapshots(snapshots, currentMc) {
    if (!snapshots || snapshots.length === 0) return null;
    const sorted = [...snapshots].sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date));
    const today = new Date().toISOString().slice(0, 10);
    const prior = sorted.filter((row) => row.snapshot_date < today);
    const baseline = prior.length > 0 ? prior[prior.length - 1] : sorted[sorted.length - 1];
    if (!baseline || baseline.mc == null) return null;
    if (baseline.snapshot_date === today) return null;
    return currentMc - baseline.mc;
}

/** Simulated trend when no historical snapshots exist. */
export function generateSimulatedTrend(currentCounts, days = 14) {
    const labels = [];
    const datasets = {};
    MATCON_CATEGORIES.forEach((c) => {
        datasets[c.key] = [];
    });
    datasets.MC = [];

    const today = new Date();
    for (let i = days - 1; i >= 0; i -= 1) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));

        const variance = (key, base) => {
            const seed = (i * 7 + key.length * 13) % 11;
            const delta = seed - 5;
            return Math.max(0, base + delta);
        };

        MATCON_CATEGORIES.forEach((c) => {
            datasets[c.key].push(variance(c.key, currentCounts[c.key] || 0));
        });
        datasets.MC.push(variance('MC', currentCounts.MC || 0));
    }

    return { labels, datasets };
}

const SNAPSHOT_KEY = 'mocDailyCallSnapshot';

export function loadMcChangeSnapshot() {
    try {
        const raw = localStorage.getItem(SNAPSHOT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export function saveMcChangeSnapshot(counts) {
    try {
        localStorage.setItem(
            SNAPSHOT_KEY,
            JSON.stringify({ date: new Date().toISOString().slice(0, 10), MC: counts.MC })
        );
    } catch {
        /* ignore quota errors */
    }
}

export function computeMcChange(currentMc, snapshot) {
    if (!snapshot || snapshot.MC == null) return null;
    const today = new Date().toISOString().slice(0, 10);
    if (snapshot.date === today) return null;
    return currentMc - snapshot.MC;
}

/** Nested MCRS / OOR groups for the Daily Status Brief (matches Aircraft Summary). */
export const BRIEF_GROUPS = [
    { key: 'MCRS', label: 'MCRS', statuses: ['UP', 'XC', 'DN', 'DS', 'PM', 'FCF'] },
    { key: 'OOR', label: 'OOR', statuses: ['AWP', 'OTH', 'AIMD', 'ACI', 'REI'] },
];

export const BRIEF_STATUSES = BRIEF_GROUPS.flatMap((g) => g.statuses);

export const BRIEF_STATUS_LABELS = {
    UP: 'UP (FMC)',
};

export function briefStatusLabel(status) {
    return BRIEF_STATUS_LABELS[status] || status;
}

export function localDateString(d = new Date()) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function shiftDateString(dateStr, days) {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + days);
    return localDateString(dt);
}

/** Normalize live aircraft-status rows into snapshot-shaped brief rows. */
export function liveRecordsToBriefRows(records) {
    return records.map((record) => {
        const normalized = normalizeStatusRecord(record);
        return {
            buno: record.buno || record.aircraft?.buno || '',
            site_name: normalized.site,
            tms_name: normalized.tms,
            status: (normalized.status || '').trim(),
            reason: record.reason || '',
            edd: record.edd || null,
            ecd: record.ecd || null,
            oth_sub_status: record.oth_sub_status || '',
            pm_status: record.pm_status || '',
        };
    });
}

export function filterBriefRows(rows, { tms = 'All' } = {}) {
    if (!tms || tms === 'All') return rows;
    return rows.filter((r) => r.tms_name === tms);
}

export function groupBriefRowsByStatus(rows, statuses = BRIEF_STATUSES) {
    return statuses.map((status) => ({
        status,
        aircraft: rows
            .filter((r) => r.status === status)
            .sort((a, b) => String(a.buno).localeCompare(String(b.buno))),
    }));
}

export function countBriefBySite(rows, statuses = BRIEF_STATUSES) {
    const bySite = {};
    uniqueSites(rows, 'site_name').forEach((site) => {
        bySite[site] = 0;
    });
    rows.forEach((row) => {
        if (!Object.prototype.hasOwnProperty.call(bySite, row.site_name)) return;
        if (statuses.length && !statuses.includes(row.status)) return;
        bySite[row.site_name] += 1;
    });
    return bySite;
}

export function countBriefByStatus(rows, statuses = BRIEF_STATUSES) {
    const counts = Object.fromEntries(statuses.map((s) => [s, 0]));
    rows.forEach((row) => {
        if (Object.prototype.hasOwnProperty.call(counts, row.status)) {
            counts[row.status] += 1;
        }
    });
    return counts;
}

export function countBriefByGroup(rows, groups = BRIEF_GROUPS) {
    const counts = {};
    groups.forEach((group) => {
        counts[group.key] = rows.filter((r) => group.statuses.includes(r.status)).length;
    });
    return counts;
}

export function buildBriefNestedGroups(rows, groups = BRIEF_GROUPS) {
    const byStatus = groupBriefRowsByStatus(rows, BRIEF_STATUSES);
    const statusMap = Object.fromEntries(byStatus.map((g) => [g.status, g.aircraft]));
    return groups.map((group) => ({
        key: group.key,
        label: group.label,
        statuses: group.statuses.map((status) => ({
            status,
            label: briefStatusLabel(status),
            aircraft: statusMap[status] || [],
        })),
        aircraft: group.statuses.flatMap((status) => statusMap[status] || []),
    }));
}
